(() => {
  const scriptSelector = 'script[src="https://www.instagram.com/embed.js"]';
  let instagramPromise;

  function processEmbeds(scope) {
    if (window.instgrm?.Embeds?.process) {
      window.instgrm.Embeds.process(scope);
    }
  }

  function loadInstagram() {
    if (window.instgrm?.Embeds?.process) return Promise.resolve();
    if (instagramPromise) return instagramPromise;

    instagramPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(scriptSelector);
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        window.setTimeout(resolve, 1200);
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.instagram.com/embed.js';
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.body.appendChild(script);
    });

    return instagramPromise;
  }

  function activateDesktopEmbeds(root) {
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    const embeds = root.querySelectorAll('[data-ales-instagram-embed]');
    if (!embeds.length) return;

    embeds.forEach((embed) => {
      if (embed.classList.contains('instagram-media')) return;
      embed.classList.add('instagram-media');
      embed.dataset.instgrmPermalink = embed.dataset.instagramUrl;
      embed.dataset.instgrmVersion = '14';
    });

    loadInstagram().then(() => processEmbeds(root)).catch(() => {});
  }

  function initializePagination(root) {
    const pagination = root.querySelector('[data-ales-snap-pagination]');
    const grid = root.querySelector('.ales-players-snap__archive-grid');
    if (!pagination || !grid) return;

    const pageSize = Number.parseInt(pagination.dataset.pageSize, 10) || 8;
    const cards = [...grid.querySelectorAll('[data-ales-snap-page-item]')]
      .map((card, index) => ({
        card,
        index,
        order: Number.parseInt(window.getComputedStyle(card).order, 10) || 0,
      }))
      .sort((left, right) => left.order - right.order || left.index - right.index)
      .map(({ card }) => card);
    const totalPages = Math.ceil(cards.length / pageSize);
    const buttonsTarget = pagination.querySelector('[data-ales-snap-page-buttons]');
    const previous = pagination.querySelector('[data-ales-snap-page-previous]');
    const next = pagination.querySelector('[data-ales-snap-page-next]');
    const status = root.querySelector('[data-ales-snap-page-status]');

    cards.forEach((card) => grid.appendChild(card));
    if (totalPages <= 1 || !buttonsTarget || !previous || !next) return;

    const requestedPage = Number.parseInt(new URL(window.location.href).searchParams.get('snap_page'), 10);
    let currentPage = Math.min(Math.max(requestedPage || 1, 1), totalPages);

    for (let page = 1; page <= totalPages; page += 1) {
      const button = document.createElement('button');
      button.className = 'ales-players-snap__pagination-page';
      button.type = 'button';
      button.dataset.alesSnapPage = String(page);
      button.textContent = String(page);
      button.setAttribute('aria-label', `ページ${page}を表示`);
      buttonsTarget.appendChild(button);
    }

    function renderPage(page, updateHistory = false) {
      currentPage = Math.min(Math.max(page, 1), totalPages);
      const firstIndex = (currentPage - 1) * pageSize;
      const lastIndex = firstIndex + pageSize;

      cards.forEach((card, index) => {
        const hidden = index < firstIndex || index >= lastIndex;
        card.classList.toggle('is-page-hidden', hidden);
        if (hidden) {
          card.setAttribute('aria-hidden', 'true');
          card.querySelectorAll('[data-ales-snap-autoplay]').forEach((video) => video.pause());
        } else {
          card.removeAttribute('aria-hidden');
        }
      });

      buttonsTarget.querySelectorAll('[data-ales-snap-page]').forEach((button) => {
        const selected = Number.parseInt(button.dataset.alesSnapPage, 10) === currentPage;
        if (selected) button.setAttribute('aria-current', 'page');
        else button.removeAttribute('aria-current');
      });

      previous.disabled = currentPage === 1;
      next.disabled = currentPage === totalPages;
      if (status) status.textContent = `${currentPage} / ${totalPages}`;
      pagination.hidden = false;

      if (updateHistory) {
        const url = new URL(window.location.href);
        if (currentPage === 1) url.searchParams.delete('snap_page');
        else url.searchParams.set('snap_page', String(currentPage));
        window.history.pushState({ alesSnapPage: currentPage }, '', url);
      }

      root.dispatchEvent(new CustomEvent('ales:snap-page-change'));
    }

    const clickHandler = (event) => {
      const pageButton = event.target.closest('[data-ales-snap-page]');
      if (pageButton) {
        renderPage(Number.parseInt(pageButton.dataset.alesSnapPage, 10), true);
      } else if (event.target.closest('[data-ales-snap-page-previous]')) {
        renderPage(currentPage - 1, true);
      } else if (event.target.closest('[data-ales-snap-page-next]')) {
        renderPage(currentPage + 1, true);
      } else {
        return;
      }

      root.querySelector('.ales-players-snap__header')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    };
    const popstateHandler = () => {
      const page = Number.parseInt(new URL(window.location.href).searchParams.get('snap_page'), 10) || 1;
      renderPage(page);
    };

    pagination.addEventListener('click', clickHandler);
    window.addEventListener('popstate', popstateHandler);
    renderPage(currentPage);

    root.alesSnapPaginationCleanup = () => {
      pagination.removeEventListener('click', clickHandler);
      window.removeEventListener('popstate', popstateHandler);
    };
  }

  function initializeProductSlider(root) {
    const slider = root.querySelector('[data-ales-snap-product-slider]');
    const track = slider?.querySelector('[data-ales-snap-product-track]');
    const slides = track ? [...track.querySelectorAll('[data-ales-snap-product-slide]')] : [];
    if (!slider || !track || !slides.length) return;

    const previous = slider.querySelector('[data-ales-snap-product-previous]');
    const next = slider.querySelector('[data-ales-snap-product-next]');
    const status = slider.querySelector('[data-ales-snap-product-status]');
    let activeIndex = -1;
    let scrollTimer;

    function slideLeft(slide) {
      return slide.offsetLeft - track.offsetLeft;
    }

    function visibleCount() {
      if (slides.length < 2) return 1;
      const slideStep = Math.abs(slideLeft(slides[1]) - slideLeft(slides[0]));
      return Math.max(1, Math.min(slides.length, Math.round(track.clientWidth / slideStep)));
    }

    function maximumIndex() {
      return Math.max(0, slides.length - visibleCount());
    }

    function nearestIndex() {
      return slides.reduce((nearest, slide, index) => {
        const currentDistance = Math.abs(slideLeft(slide) - track.scrollLeft);
        const nearestDistance = Math.abs(slideLeft(slides[nearest]) - track.scrollLeft);
        return currentDistance < nearestDistance ? index : nearest;
      }, 0);
    }

    function setActive(index, notify = true) {
      const nextIndex = Math.min(Math.max(index, 0), maximumIndex());
      const changed = nextIndex !== activeIndex;
      activeIndex = nextIndex;
      root.alesSnapProductActiveVideo = slides[activeIndex].querySelector('[data-ales-snap-autoplay]');

      slides.forEach((slide, slideIndex) => {
        if (slideIndex === activeIndex) slide.setAttribute('aria-current', 'true');
        else slide.removeAttribute('aria-current');
      });

      if (previous) previous.disabled = activeIndex === 0;
      if (next) next.disabled = activeIndex === maximumIndex();
      if (status) {
        const lastVisible = Math.min(activeIndex + visibleCount(), slides.length);
        status.textContent = lastVisible === activeIndex + 1
          ? `${activeIndex + 1} / ${slides.length}`
          : `${activeIndex + 1}–${lastVisible} / ${slides.length}`;
      }
      if (notify && changed) root.dispatchEvent(new CustomEvent('ales:snap-slider-change'));
    }

    function goTo(index) {
      const nextIndex = Math.min(Math.max(index, 0), maximumIndex());
      setActive(nextIndex);
      track.scrollTo({ left: slideLeft(slides[nextIndex]), behavior: 'smooth' });
    }

    const clickHandler = (event) => {
      if (event.target.closest('[data-ales-snap-product-previous]')) goTo(activeIndex - 1);
      if (event.target.closest('[data-ales-snap-product-next]')) goTo(activeIndex + 1);
    };
    const scrollHandler = () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => setActive(nearestIndex()), 120);
    };
    const resizeHandler = () => setActive(nearestIndex());

    slider.addEventListener('click', clickHandler);
    track.addEventListener('scroll', scrollHandler, { passive: true });
    window.addEventListener('resize', resizeHandler);
    setActive(0, false);

    root.alesSnapProductSliderCleanup = () => {
      window.clearTimeout(scrollTimer);
      slider.removeEventListener('click', clickHandler);
      track.removeEventListener('scroll', scrollHandler);
      window.removeEventListener('resize', resizeHandler);
      root.alesSnapProductActiveVideo = null;
    };
  }

  function initializeAutoplay(root) {
    const videos = [...root.querySelectorAll('[data-ales-snap-autoplay]')];
    if (!videos.length) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const saveData = navigator.connection?.saveData === true;
    const productTrack = root.querySelector('[data-ales-snap-product-track]');
    let sectionVisible = false;

    function prepare(video) {
      if (video.src || !video.dataset.src) return;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.src = video.dataset.src;
      video.load();
    }

    function playAll() {
      if (!sectionVisible || document.hidden || reducedMotion.matches || saveData) return;

      const playableVideos = productTrack
        ? [root.alesSnapProductActiveVideo || productTrack.querySelector('[data-ales-snap-autoplay]')].filter(Boolean)
        : videos.filter((video) => !video.closest('.is-page-hidden'));
      const playableSet = new Set(playableVideos);

      videos.forEach((video) => {
        if (!playableSet.has(video)) {
          video.pause();
          return;
        }
        prepare(video);
        video.play().catch(() => {
          video.closest('.ales-players-snap-card__media')?.classList.remove('is-playing');
        });
      });
    }

    function pauseAll() {
      videos.forEach((video) => video.pause());
    }

    root.alesSnapPlayAutoplay = playAll;
    root.alesSnapPauseAutoplay = pauseAll;

    videos.forEach((video) => {
      const media = video.closest('.ales-players-snap-card__media');
      video.addEventListener('playing', () => media?.classList.add('is-playing'));
      video.addEventListener('error', () => media?.classList.remove('is-playing'));
    });

    const observer = new IntersectionObserver((entries) => {
      sectionVisible = entries.some((entry) => entry.isIntersecting);
      if (sectionVisible) playAll();
      else pauseAll();
    }, { rootMargin: productTrack ? '80px 0px' : '240px 0px', threshold: 0.01 });

    const visibilityHandler = () => {
      if (document.hidden) pauseAll();
      else playAll();
    };
    const motionHandler = () => {
      if (reducedMotion.matches) pauseAll();
      else playAll();
    };
    const sliderChangeHandler = () => {
      pauseAll();
      playAll();
    };

    observer.observe(root);
    document.addEventListener('visibilitychange', visibilityHandler);
    reducedMotion.addEventListener?.('change', motionHandler);
    root.addEventListener('ales:snap-page-change', playAll);
    root.addEventListener('ales:snap-slider-change', sliderChangeHandler);

    root.alesSnapAutoplayCleanup = () => {
      observer.disconnect();
      pauseAll();
      document.removeEventListener('visibilitychange', visibilityHandler);
      reducedMotion.removeEventListener?.('change', motionHandler);
      root.removeEventListener('ales:snap-page-change', playAll);
      root.removeEventListener('ales:snap-slider-change', sliderChangeHandler);
      root.alesSnapPlayAutoplay = null;
      root.alesSnapPauseAutoplay = null;
    };
  }

  function closeModal(modal) {
    modal.alesSnapEmbedObserver?.disconnect();
    modal.alesSnapEmbedObserver = null;

    const embedTarget = modal.querySelector('[data-ales-snap-modal-embed]');
    if (embedTarget) embedTarget.replaceChildren();

    if (typeof modal.close === 'function' && modal.open) {
      modal.close();
    } else {
      modal.removeAttribute('open');
    }

    modal.closest('[data-ales-players-snap]')?.alesSnapPlayAutoplay?.();
  }

  function openModal(root, trigger) {
    const modal = root.querySelector('[data-ales-snap-modal]');
    const card = trigger.closest('.ales-players-snap-card');
    const embedTarget = modal?.querySelector('[data-ales-snap-modal-embed]');
    const title = modal?.querySelector('[data-ales-snap-modal-title]');
    const fallback = modal?.querySelector('[data-ales-snap-modal-fallback]');
    const account = modal?.querySelector('[data-ales-snap-modal-account]');
    const comment = modal?.querySelector('[data-ales-snap-modal-comment]');
    const productName = modal?.querySelector('[data-ales-snap-modal-product-name]');
    const productLink = modal?.querySelector('[data-ales-snap-modal-product-link]');
    const getData = (key) => trigger.dataset[key] || card?.dataset[key] || '';
    const url = getData('instagramUrl');
    if (!modal || !embedTarget || !url) return;

    root.alesSnapPauseAutoplay?.();

    if (title) title.textContent = getData('modalTitle') || 'Instagram投稿';
    if (fallback) fallback.href = url;

    if (account) {
      account.textContent = getData('modalAccount');
      account.hidden = !account.textContent;
    }

    if (comment) {
      comment.textContent = getData('modalComment');
      comment.hidden = !comment.textContent;
    }

    if (productName && productLink && getData('productUrl')) {
      productName.textContent = getData('productTitle') || '関連商品';
      productName.hidden = false;
      productLink.href = getData('productUrl');
      productLink.hidden = false;
    } else if (productName && productLink) {
      productName.hidden = true;
      productLink.hidden = true;
    }

    const cardImage = card?.querySelector('.ales-players-snap-card__image');
    const preview = document.createElement('div');
    preview.className = 'ales-players-snap-modal__preview';

    if (cardImage?.currentSrc || cardImage?.src) {
      const previewImage = document.createElement('img');
      previewImage.className = 'ales-players-snap-modal__preview-image';
      previewImage.src = cardImage.currentSrc || cardImage.src;
      previewImage.alt = cardImage.alt || '';
      preview.appendChild(previewImage);

      if (card.querySelector('.ales-players-snap-card__play')) {
        const play = document.createElement('span');
        play.className = 'ales-players-snap-modal__preview-play';
        play.setAttribute('aria-hidden', 'true');
        play.textContent = '▶';
        preview.appendChild(play);
      }
    }

    const blockquote = document.createElement('blockquote');
    blockquote.className = 'instagram-media';
    blockquote.dataset.instgrmPermalink = url;
    blockquote.dataset.instgrmVersion = '14';
    blockquote.style.margin = '0 auto';
    blockquote.style.maxWidth = '100%';
    blockquote.style.minWidth = '0';
    blockquote.style.width = '100%';
    embedTarget.replaceChildren(...(preview.childElementCount ? [preview, blockquote] : [blockquote]));

    if (preview.childElementCount) {
      const observer = new MutationObserver(() => {
        if (embedTarget.querySelector('iframe')) {
          preview.hidden = true;
          observer.disconnect();
          modal.alesSnapEmbedObserver = null;
        }
      });
      observer.observe(embedTarget, { childList: true, subtree: true });
      modal.alesSnapEmbedObserver = observer;
    }

    if (typeof modal.showModal === 'function') {
      modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }

    modal.alesSnapTrigger = trigger;

    loadInstagram().then(() => processEmbeds(modal)).catch(() => {});
  }

  function initialize(root) {
    if (!root || root.dataset.alesPlayersSnapInitialized === 'true') return;
    root.dataset.alesPlayersSnapInitialized = 'true';

    activateDesktopEmbeds(root);
    initializeProductSlider(root);
    initializePagination(root);
    initializeAutoplay(root);

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-ales-snap-open]');
      if (trigger && root.contains(trigger)) {
        openModal(root, trigger);
        return;
      }

      const close = event.target.closest('[data-ales-snap-modal-close]');
      if (close) closeModal(close.closest('[data-ales-snap-modal]'));

      const modal = event.target.closest('[data-ales-snap-modal]');
      if (modal && event.target === modal) closeModal(modal);
    });

    root.querySelectorAll('[data-ales-snap-modal]').forEach((modal) => {
      modal.addEventListener('close', () => {
        modal.alesSnapEmbedObserver?.disconnect();
        modal.alesSnapEmbedObserver = null;
        const embedTarget = modal.querySelector('[data-ales-snap-modal-embed]');
        if (embedTarget) embedTarget.replaceChildren();
        modal.alesSnapTrigger?.focus();
        modal.alesSnapTrigger = null;
        root.alesSnapPlayAutoplay?.();
      });
    });
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll('[data-ales-players-snap]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-ales-players-snap]').forEach((root) => {
      root.alesSnapAutoplayCleanup?.();
      root.alesSnapPaginationCleanup?.();
      root.alesSnapProductSliderCleanup?.();
    });
  });
})();
